import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import { downloadCsv } from '../../lib/csv';
import { usePackageSubscribers, useAdminAllPackages } from '../../api/admin';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'info',
  past_due: 'warning',
  suspended: 'error',
  canceled: 'error',
  expired: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
  expired: 'Expired',
};

const STATUS_COLOR: Record<string, 'emerald' | 'amber' | 'rose' | 'zinc'> = {
  active: 'emerald',
  trial: 'amber',
  past_due: 'rose',
  suspended: 'rose',
  canceled: 'zinc',
  expired: 'zinc',
};

const statusLabel = (s: string) => STATUS_LABEL[s] ?? s;
const statusTone = (s: string) => STATUS_TONE[s] ?? 'neutral';

/** Distinct values (order preserved) with counts, sorted by count desc. */
function tally<T>(rows: T[], key: (r: T) => string) {
  const m = new Map<string, number>();
  for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }));
}

/** Subscribers per package — by-price chart, by-status, by-reseller, subscriber table. All real. */
export function PackageSubscribers() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: subscribers = [], isLoading } = usePackageSubscribers(id ?? '');
  const { data: allPackages = [] } = useAdminAllPackages();
  const pkg = allPackages.find((p) => p.id === id);

  const [statusFilter, setStatusFilter] = useState('All');
  const [priceFilter, setPriceFilter] = useState('All');
  const [resellerFilter, setResellerFilter] = useState('All');

  const total = subscribers.length;
  const activeCount = subscribers.filter((s) => s.status === 'active').length;

  const byPrice = useMemo(() => tally(subscribers, (s) => s.price), [subscribers]);
  const byStatus = useMemo(() => tally(subscribers, (s) => s.status), [subscribers]);
  const byReseller = useMemo(() => tally(subscribers, (s) => s.reseller), [subscribers]);

  const priceOptions = useMemo(() => byPrice.map((p) => p.label), [byPrice]);
  const resellerOptions = useMemo(() => byReseller.map((r) => r.label), [byReseller]);
  const statusOptions = useMemo(() => byStatus.map((s) => s.label), [byStatus]);

  const filtered = subscribers.filter(
    (s) =>
      (statusFilter === 'All' || s.status === statusFilter) &&
      (priceFilter === 'All' || s.price === priceFilter) &&
      (resellerFilter === 'All' || s.reseller === resellerFilter),
  );

  const exportCsv = () =>
    downloadCsv(
      `${pkg?.name ?? 'package'}-subscribers`,
      ['Business', 'Reseller', 'Price', 'Status', 'Renews'],
      filtered.map((s) => [s.business, s.reseller, s.price, statusLabel(s.status), s.renews ? formatDate(s.renews) : '']),
    );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        All packages
      </Link>

      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">Pricing</p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          {pkg?.name ?? 'Package'} — subscribers{' '}
          <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({activeCount} active)</span>
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">Critical view for grandfathering decisions and migration planning.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="p-5 sm:p-6">
          <CardHeader title="By price" subtitle="Multi-currency, multi-interval" />
          <div className="space-y-3">
            {byPrice.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">No subscribers yet.</p>
            ) : (
              byPrice.map((p) => <PriceBar key={p.label} label={p.label} count={p.count} pct={total ? Math.round((p.count / total) * 100) : 0} />)
            )}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="By status" />
          <div className="grid grid-cols-2 gap-3">
            {byStatus.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400 col-span-2">—</p>
            ) : (
              byStatus.map((s) => <StatusStat key={s.label} n={s.count} label={statusLabel(s.label)} color={STATUS_COLOR[s.label] ?? 'zinc'} />)
            )}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="By reseller" />
          <ul className="space-y-2.5 text-sm">
            {byReseller.length === 0 ? (
              <li className="text-zinc-500 dark:text-zinc-400">—</li>
            ) : (
              byReseller.map((r) => <ResellerRow key={r.label} name={r.label} count={r.count} pct={total ? Math.round((r.count / total) * 100) : 0} />)
            )}
          </ul>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mr-auto">Subscriber list</h2>
          <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={statusOptions} render={statusLabel} />
          <FilterSelect label="Price" value={priceFilter} onChange={setPriceFilter} options={priceOptions} />
          <FilterSelect label="Reseller" value={resellerFilter} onChange={setResellerFilter} options={resellerOptions} />
          <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" />} onClick={exportCsv} disabled={filtered.length === 0}>
            Export CSV
          </Button>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3">Reseller</th>
                <th className="text-left font-medium px-4 py-3">Price</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Renews</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-40 max-w-full rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-28 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-24 rounded" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {total === 0 ? 'No subscribers found.' : 'No subscribers match these filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3 font-medium text-zinc-950 dark:text-zinc-50">{s.business}</td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{s.reseller}</td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{s.price}</td>
                    <td className="px-4 py-3">
                      <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{s.renews ? formatDate(s.renews) : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PriceBar({ label, count, pct }: { label: string; count: number; pct: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">{count}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-brand dark:bg-brand-dm rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusStat({ n, label, color }: { n: number; label: string; color: 'emerald' | 'amber' | 'rose' | 'zinc' }) {
  const cls = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    rose: 'text-rose-700 dark:text-rose-300',
    zinc: 'text-zinc-700 dark:text-zinc-200',
  }[color];
  return (
    <div className="rounded-md p-3 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
      <div className={cn('text-2xl font-semibold tabular-nums', cls)}>{n}</div>
      <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{label}</div>
    </div>
  );
}

function ResellerRow({ name, count, pct }: { name: string; count: number; pct: number }) {
  return (
    <li>
      <div className="flex items-center justify-between mb-1">
        <span className="text-zinc-700 dark:text-zinc-200">{name}</span>
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
          {count} <span className="opacity-60">({pct}%)</span>
        </span>
      </div>
      <div className="h-1 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-zinc-500 dark:bg-zinc-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  render,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  render?: (v: string) => string;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition cursor-pointer">
      <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent outline-none text-zinc-800 dark:text-zinc-100 cursor-pointer -mr-1"
      >
        <option value="All">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {render ? render(o) : o}
          </option>
        ))}
      </select>
    </label>
  );
}
